import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, CardActionArea, type SxProps, type Theme } from '@mui/material';

interface MobileCardListProps<T> {
  items: T[];
  renderCard: (item: T, index: number) => React.ReactNode;
  onItemClick?: (item: T) => void;
  /** Real card link so middle-click and "Open in new tab" work (#4108). */
  getItemHref?: (item: T) => string;
  sx?: SxProps<Theme>;
}

function isModifiedClick(event: React.MouseEvent): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

export function MobileCardList<T>({
  items,
  renderCard,
  onItemClick,
  getItemHref,
  sx,
}: MobileCardListProps<T>) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, ...sx }}>
      {items.map((item, index) => {
        const href = getItemHref?.(item);
        const card = renderCard(item, index);
        if (href) {
          return (
            <Card key={index} variant="outlined" sx={{ position: 'relative' }}>
              <CardActionArea
                component={RouterLink}
                to={href}
                onClick={(event: React.MouseEvent) => {
                  if (isModifiedClick(event)) return;
                  if (event.target instanceof Element) {
                    const interactive = event.target.closest(
                      'a, button, input, textarea, select, [role="button"]',
                    );
                    if (interactive && interactive !== event.currentTarget) {
                      event.preventDefault();
                      return;
                    }
                  }
                  if (!onItemClick) return;
                  event.preventDefault();
                  onItemClick(item);
                }}
                sx={{
                  p: 2,
                  '& .MuiButtonBase-root, & input, & textarea, & select': {
                    position: 'relative',
                    zIndex: 1,
                  },
                }}
              >
                {card}
              </CardActionArea>
            </Card>
          );
        }
        if (onItemClick) {
          return (
            <Card key={index} variant="outlined">
              <CardActionArea onClick={() => onItemClick(item)} sx={{ p: 2 }}>
                {card}
              </CardActionArea>
            </Card>
          );
        }
        return (
          <Card key={index} variant="outlined" sx={{ p: 2 }}>
            {card}
          </Card>
        );
      })}
    </Box>
  );
}
